<template>
  <div class="app-product-aid">
    <div class="app-product-aid__wrapper">
      <AppProductAidFooter
        v-if="currentView !== 'intro'"
        :show-counter="currentView === 'swipe'"
        :counter="currentView === 'swipe' ? `${String(activeIndex + 1).padStart(2, '0')}/${topics.length}` : undefined"
        @back="handleBackNavigation"
        @reset="handleFullReset"
      />
      <!-- Intro View (image + start button) -->
      <AppProductAidIntro v-if="currentView === 'intro'" :intro-image @start="handleStart" />

      <!-- To Start (tag selection) View -->
      <AppProductAidToStart
        v-else-if="currentView === 'tags'"
        v-model="selectedTagIds"
        :title="toStartTitle"
        :intro="toStartIntro"
        :tags
        @select="handleTagsSelect"
      />

      <!-- Swipe Explanation View -->
      <AppProductAidExplanation
        v-else-if="currentView === 'explanation'"
        :title="introTitle"
        :like-icon
        :intro-instruction-like
        :dislike-icon
        :intro-instruction-dislike
        @start="currentView = 'swipe'"
      />

      <div v-else-if="currentView === 'swipe'" class="app-product-aid__content">
        <AppProductAidCardStack
          ref="cardStackRef"
          :topics
          :active-index
          :like-indicator-opacity
          :dislike-indicator-opacity
          @like="handleLike"
          @dislike="handleDislike"
          @update:like-indicator-opacity="likeIndicatorOpacity = $event"
          @update:dislike-indicator-opacity="dislikeIndicatorOpacity = $event"
        />

        <AppProductAidSwipeActions
          :disabled="activeIndex >= topics.length"
          @like="handleLike"
          @dislike="handleDislike"
        />
      </div>

      <AppProductAidResults
        v-else-if="currentView === 'results' || currentView === 'regional'"
        :view="currentView"
        :winner="winnerResult"
        :secondary-results
        :regional-products
        :expanded-id="expandedResultId"
        @select-regional="handleSelectRegional"
        @update:expanded-id="expandedResultId = $event"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { ProductAidProduct } from './AppProductAidResults.vue';

  export type ProductAidItem = {
    id: string;
    image: string;
    alt?: string;
    products: string[];
  };

  export type ProductAidTag = {
    id: string;
    text: string;
    products: string[];
  };

  type HistoryEntry = {
    index: number;
    action: 'like' | 'dislike';
    topic: ProductAidItem;
  };

  const { t } = useI18n();

  const {
    items = [],
    products = [],
    tags = [],
    toStartTitle = '',
    toStartIntro = '',
    regionalIntro = '',
  } = defineProps<{
    introTitle: string;
    likeIcon: string;
    introInstructionLike: string;
    dislikeIcon: string;
    introInstructionDislike: string;
    items?: ProductAidItem[];
    products?: ProductAidProduct[];
    introImage: string;
    toStartTitle?: string;
    toStartIntro?: string;
    /** Result text for the combined "Krant uit je regio" option, which has no product of its own. */
    regionalIntro?: string;
    tags?: ProductAidTag[];
  }>();
  // Shuffled client-side (on mount) so SSR output stays deterministic
  const topics = ref<ProductAidItem[]>(items);
  onMounted(() => {
    topics.value = shuffleItems(items);
  });

  function shuffleItems(list: ProductAidItem[]) {
    const shuffled = [...list];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const current = shuffled[i];
      const target = shuffled[j];
      if (!current || !target) continue;

      shuffled[i] = target;
      shuffled[j] = current;
    }
    return shuffled;
  }
  const currentView = ref<'intro' | 'tags' | 'explanation' | 'swipe' | 'results' | 'regional'>('intro');
  const selectedTagIds = ref<string[]>([]);
  const appliedTagCodes = ref<string[][]>([]);
  const expandedResultId = ref<string | null>(null);
  const likeMap = ref(new Map<string, number>());
  const dislikeMap = ref(new Map<string, number>());
  const activeIndex = ref(0);
  const history = ref<HistoryEntry[]>([]);
  const likeIndicatorOpacity = ref(0);
  const dislikeIndicatorOpacity = ref(0);
  const cardStackRef = ref<{
    animateCardOut: (direction: 'left' | 'right', onComplete: () => void) => void;
    animateCardIn: (index: number, direction: 'left' | 'right') => void;
    resetCards: () => void;
  } | null>(null);

  const nonRegionalProducts = computed(() => products.filter((p) => !p.isRegional));
  const regionalProducts = computed(() => products.filter((p) => p.isRegional));

  // Regional codes Set for quick lookup
  const regionalCodes = computed(() => new Set(regionalProducts.value.map((p) => p.code)));

  // Calculate total likes for regional products combined
  // Uses a special 'regional-group' key to count regional likes as a single group
  const regionalTotalLikes = computed(() => likeMap.value.get('regional-group') || 0);

  // Calculate likes for non-regional products and regional as one group
  const totalLikes = computed(() => {
    let total = regionalTotalLikes.value;
    for (const product of nonRegionalProducts.value) {
      total += likeMap.value.get(product.code) || 0;
    }
    return total;
  });

  const rankedResults = computed(() => {
    if (totalLikes.value === 0) return [];

    const results: { product: ProductAidProduct; percentage: number; likes: number; isRegionalGroup?: boolean }[] = [];

    // Add non-regional products individually
    for (const product of nonRegionalProducts.value) {
      const likes = likeMap.value.get(product.code) || 0;
      if (likes > 0) {
        const percentage = Math.round((likes / totalLikes.value) * 100);
        results.push({ product, percentage, likes });
      }
    }

    // Add regional products as ONE combined option
    const [firstRegionalProduct] = regionalProducts.value;
    if (regionalTotalLikes.value > 0 && firstRegionalProduct) {
      const percentage = Math.round((regionalTotalLikes.value / totalLikes.value) * 100);
      // Use first regional product as representative, but mark it as a group
      const representativeProduct: ProductAidProduct = {
        code: 'regional-group',
        title: t('marketingPages.productAid.regionalNewspaper'),
        intro: regionalIntro,
        image: firstRegionalProduct.image,
        link: '',
        isRegional: true,
      };
      results.push({
        product: representativeProduct,
        percentage,
        likes: regionalTotalLikes.value,
        isRegionalGroup: true,
      });
    }

    // Sort by percentage descending
    results.sort((a, b) => b.percentage - a.percentage);

    return results;
  });

  const winnerResult = computed(() => rankedResults.value[0] || null);
  const secondaryResults = computed(() => rankedResults.value.slice(1));

  // Watch for swiping completion to transition to results
  watch(activeIndex, (newIndex) => {
    if (newIndex >= topics.value.length && currentView.value === 'swipe') {
      currentView.value = 'results';
    }
  });

  // Add one point per product code; regional codes count ONCE combined via the 'regional-group' key
  function addPoints(map: Map<string, number>, productCodes: string[]) {
    let hasRegional = false;

    for (const productCode of productCodes) {
      if (productCode) {
        if (regionalCodes.value.has(productCode)) {
          hasRegional = true;
        } else {
          const currentCount = map.get(productCode) || 0;
          map.set(productCode, currentCount + 1);
        }
      }
    }

    if (hasRegional) {
      const currentCount = map.get('regional-group') || 0;
      map.set('regional-group', currentCount + 1);
    }
  }

  function removePoints(map: Map<string, number>, productCodes: string[]) {
    let hasRegional = false;

    for (const productCode of productCodes) {
      if (productCode) {
        if (regionalCodes.value.has(productCode)) {
          hasRegional = true;
        } else {
          const currentCount = map.get(productCode) || 0;
          if (currentCount <= 1) {
            map.delete(productCode);
          } else {
            map.set(productCode, currentCount - 1);
          }
        }
      }
    }

    if (hasRegional) {
      const currentCount = map.get('regional-group') || 0;
      if (currentCount <= 1) {
        map.delete('regional-group');
      } else {
        map.set('regional-group', currentCount - 1);
      }
    }
  }

  function handleLike() {
    const topic = topics.value[activeIndex.value];
    if (!topic) return;

    addPoints(likeMap.value, topic.products);

    // Force reactivity update
    likeMap.value = new Map(likeMap.value);

    history.value.push({
      index: activeIndex.value,
      action: 'like',
      topic,
    });

    // Animate card out via card stack
    cardStackRef.value?.animateCardOut('right', () => {
      activeIndex.value++;
    });
  }

  function handleDislike() {
    const topic = topics.value[activeIndex.value];
    if (!topic) return;

    addPoints(dislikeMap.value, topic.products);

    // Force reactivity update
    dislikeMap.value = new Map(dislikeMap.value);

    history.value.push({
      index: activeIndex.value,
      action: 'dislike',
      topic,
    });

    // Animate card out via card stack
    cardStackRef.value?.animateCardOut('left', () => {
      activeIndex.value++;
    });
  }

  function handleBack() {
    if (history.value.length === 0) return;

    const lastEntry = history.value.pop();
    if (!lastEntry) return;

    // Remove from the appropriate map for each product code
    const map = lastEntry.action === 'like' ? likeMap.value : dislikeMap.value;
    removePoints(map, lastEntry.topic.products);

    // Force reactivity update
    if (lastEntry.action === 'like') {
      likeMap.value = new Map(likeMap.value);
    } else {
      dislikeMap.value = new Map(dislikeMap.value);
    }

    activeIndex.value = lastEntry.index;

    // Animate card back in via card stack
    const direction = lastEntry.action === 'like' ? 'right' : 'left';
    cardStackRef.value?.animateCardIn(lastEntry.index, direction);
  }

  function handleStart() {
    currentView.value = tags.length > 0 ? 'tags' : 'explanation';
  }

  // Selected tags award a like point per product code, same as a swiped like
  function handleTagsSelect() {
    const selectedTags = tags.filter((tag) => selectedTagIds.value.includes(tag.id));

    for (const tag of selectedTags) {
      addPoints(likeMap.value, tag.products);
      appliedTagCodes.value.push(tag.products);
    }

    // Force reactivity update
    likeMap.value = new Map(likeMap.value);

    currentView.value = 'explanation';
  }

  function undoTagPoints() {
    for (const productCodes of appliedTagCodes.value) {
      removePoints(likeMap.value, productCodes);
    }
    appliedTagCodes.value = [];

    // Force reactivity update
    likeMap.value = new Map(likeMap.value);
  }

  function handleReset() {
    likeMap.value = new Map();
    dislikeMap.value = new Map();
    history.value = [];
    activeIndex.value = 0;
    selectedTagIds.value = [];
    appliedTagCodes.value = [];
    topics.value = shuffleItems(items);

    // Reset all cards via card stack
    cardStackRef.value?.resetCards();
  }

  // Navigation functions for results view
  function handleSelectRegional() {
    currentView.value = 'regional';
    expandedResultId.value = null;
  }

  function handleBackNavigation() {
    if (currentView.value === 'regional') {
      currentView.value = 'results';
      expandedResultId.value = null;
    } else if (currentView.value === 'results') {
      // Go back to last card
      if (activeIndex.value > 0) {
        activeIndex.value = topics.value.length - 1;
        currentView.value = 'swipe';
      }
    } else if (currentView.value === 'swipe') {
      if (history.value.length > 0) {
        handleBack();
      } else {
        currentView.value = 'explanation';
      }
    } else if (currentView.value === 'explanation') {
      if (tags.length > 0) {
        // Back to tag selection: undo the points the tags awarded so re-selecting doesn't double-count
        undoTagPoints();
        currentView.value = 'tags';
      } else {
        currentView.value = 'intro';
      }
    } else if (currentView.value === 'tags') {
      currentView.value = 'intro';
    }
  }

  function handleFullReset() {
    handleReset();
    currentView.value = tags.length > 0 ? 'tags' : 'explanation';
    expandedResultId.value = null;
  }
</script>

<style lang="postcss" scoped>
  .app-product-aid {
    display: flex;
    flex-direction: column;
    background: var(--colors-primary-50);

    &__wrapper {
      display: flex;
      flex-direction: column;
      min-height: calc(100vh - 4.1875rem);
      padding: var(--spacing-6) var(--spacing-4);

      @media (--screen-sm) {
        min-height: 800px;
      }

      @media (--screen-lg) {
        align-self: center;
        width: 375px;
        min-height: 630px;
      }
    }

    &__content {
      width: 100%;
      margin: auto;
    }
  }
</style>
